import { checkEnvPurpleZone, getCookie, setCookie } from '@common/Utils';
import { appConfig } from '@common/config';
import { ResultWrapFavorite } from '@components/ResultWrapFavorite';
import { ServerDropdown, type IServerDDProp } from '@components/ServerDropdown';
import { Pagination } from '@components/pagination/Pagination';
import { PURPLEZONE_SERVER_ID_KEY } from '@constants/common';
import { useSetSearchParams } from '@hook/useSetSearchParams';
import { type ISearchParam } from '@interface/ICommon';
import { type IPriceItemList, type IPriceParams } from '@interface/IItemPrice';
import { ItemPriceQueryKey } from '@network/queryKeys';
import { useDataQuery } from '@network/querys';
import { keepPreviousData } from '@tanstack/query-core';
import { type TFunction } from 'i18next';
import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { PriceList } from './PriceList';

export const ResultWrap = ({ params }: { params: IPriceParams }) => {
    const { t } = useTranslation();
    const setParams = useSetSearchParams();
    const [svrid, svridSet] = useState('');
    const firSvrId = useMemo(() => getFirstSvrId(params.paramServerid), [params.paramServerid]);
    const [priceParams, priceParamsSet] = useState<ISearchParam>({});

    useEffect(() => {
        const svrId = params.paramServerid || firSvrId;
        svridSet(svrId);

        const para: Record<string, string | number | null> = {
            size: 30,
            serverId: svrId === 'all' ? '' : svrId,
            grades: params.paramGrade,
            classes: params.paramClass,
            page: params.paramPage,
            sale: params.paramSale ?? 'true',
            fromEnchantLevel: params.paramEnchantfrom,
            toEnchantLevel: params.paramEnchantto,
            category1: params.paramCategory1,
            category2: params.paramCategory2,
            blessedType: params.paramBless,
            searchKeyword: params.paramQuery,
            favorite: params.paramFavorite,
            exact: params.paramExactsearch,
        };
        const filteredParams = Object.fromEntries(Object.entries(para).filter(([_, value]) => value !== undefined && value !== ''));
        priceParamsSet(filteredParams as ISearchParam);
    }, [firSvrId, params]);

    const { data, isFetching, error } = useDataQuery<IPriceItemList>(
        ItemPriceQueryKey,
        `/${appConfig.service}/v2.0/price/search/item`,
        priceParams,
        {
            staleTime: 5000,
            placeholderData: keepPreviousData,
            enabled: !!priceParams?.size,
        }
    );

    const svrDropdownProp: IServerDDProp = {
        defaultSvrId: firSvrId,
        changeSvrId: svrid,
        tradableWorld: false,
        useAllSvrBtn: true,
        useWorldSvr: false,
        submitCallback: (_svr: string, _isFirstSetting?: boolean) => {
            setParams({
                serverid: _svr,
                ...(!_isFirstSetting ? { page: '1' } : {}),
            });
            setCookie('gameinforecentsvrid' + appConfig.service, _svr, 7, '.plaync.com');
        },
    };

    return (
        <div className="gameinfo-result-wrapper">
            <div className="gameinfo-result">
                <section className="gameinfo-result__util">
                    <ServerDropdown props={svrDropdownProp} />
                    <div className="title">
                        {t('noti.searchresults')}&nbsp;
                        {data && <CountOfSearchResults total={data.pagination.total} limit={data.pagination.limit} t={t} />}
                    </div>
                    <ResultWrapFavorite activeFavorite={params.paramFavorite} />
                </section>

                {data && (
                    <PriceList
                        listData={data.contents}
                        priceListParam={priceParams}
                        loading={isFetching}
                        firstSvrId={firSvrId}
                        params={params}
                    />
                )}

                {!!error && (
                    <div className="nodata" style={{ display: 'block' }}>
                        <Trans i18nKey={'common.dataerror'} />
                    </div>
                )}

                {data?.contents && data.contents.length > 0 && (
                    <>
                        <OpenApiBtn />
                        <Pagination dat={data.pagination} />
                    </>
                )}
            </div>
        </div>
    );
};

const CountOfSearchResults = ({ total, limit, t }: { total: number; limit: number; t: TFunction<'translation', undefined> }) => (
    <em>
        <Trans>{t('noti.per').replace('{N}', `${total > limit ? limit + '+' : total}`)}</Trans>
    </em>
);

const OpenApiBtn = () =>
    appConfig.service === 'l2m' && (
        <a className="link-developers" href="https://developers.plaync.com/" style={{ display: 'flex' }}>
            아이템 시세 오픈 API 이용하기
        </a>
    );

const getFirstSvrId = (_paramSvrid: string | null) => {
    // 우선순위 : 서치파람 > 퍼플존파람-pzsic > 유저데이타 > 쿠키저장 > 전체 서버
    // case: 퍼플존 (페이지 진입시, sessionStorage에 저장된 pzsid, pzcid 값 사용)
    if (_paramSvrid) return _paramSvrid;

    if (checkEnvPurpleZone()) {
        const pzsid = sessionStorage.getItem(PURPLEZONE_SERVER_ID_KEY);
        if (pzsid) return pzsid;
    }

    if (window.userData?.gameAccount?.serverId) return window.userData.gameAccount.serverId;

    const recentSvrId = getCookie('gameinforecentsvrid' + appConfig.service);
    if (recentSvrId) return recentSvrId;

    return 'all';
};
